import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaFacebookSquare,
  FaYoutubeSquare,
  FaInstagram,
  FaLinkedin,
  FaFacebookF,
  FaWhatsapp
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { store } from '../App';
import { apiUrl } from './Constants';
import '../css/Footer.css';

function Footer() {
  const [lang] = useContext(store);
  const [footerData, setFooterData] = useState(null);
  const [productsList, setProductsList] = useState([]);

  useEffect(() => {
    const baseApi = apiUrl || 'https://dev10.sbagh.com/api/';

    // 1. Fetch dynamic footer data (useful links, contact info, site settings)
    axios
      .get(`${baseApi}geFooterData`)
      .then((res) => {
        if (res?.data?.data) {
          setFooterData(res.data.data);
        }
      })
      .catch((err) => {
        console.warn('Could not load footer data:', err?.message);
      });

    // 2. Fetch menus to dynamically populate only existing products from database
    axios
      .get(`${baseApi}getMenus`)
      .then((res) => {
        if (res?.data?.data?.menus) {
          const menus = res.data.data.menus;

          // Extract products from children of models/products menu
          const modelsMenu = menus.find((m) => m.id === 3 || m.slug === 'models');
          const children = modelsMenu?.children || [];

          // Extract any standalone vehicle products
          const vehicleSlugs = ['mini-truck', 'van', 'mini-bus', 'pickup', 'light-duty-trucks', 'medium-duty-truck'];
          const standaloneVehicles = menus.filter(
            (m) => vehicleSlugs.includes(m.slug) && !children.some((c) => c.id === m.id || c.slug === m.slug)
          );

          const combined = [...children, ...standaloneVehicles];
          const seen = new Set();
          const uniqueProducts = [];
          for (const item of combined) {
            const key = item.slug || item.id;
            if (key && !seen.has(key)) {
              seen.add(key);
              uniqueProducts.push(item);
            }
          }
          setProductsList(uniqueProducts);
        }
      })
      .catch((err) => {
        console.warn('Could not load menus for footer products:', err?.message);
      });
  }, []);

  const siteInfo = footerData?.about_foton?.content || {};
  const contactInfo = footerData?.contact_information?.content || {};
  const usefulLinks = footerData?.usefull_links?.content || [];

  // Dynamic Contact Info (only what is available in DB)
  const phoneNumber = contactInfo?.contact_number || siteInfo?.contact_number || siteInfo?.telephone_number;
  const emailAddress = contactInfo?.email || siteInfo?.email;
  const cleanPhone = (phoneNumber || '').replace(/[^0-9+]/g, '');

  // Dynamic Social Media Links (only what is available in DB)
  const facebookUrl = siteInfo?.facebook_url?.trim() || null;
  const twitterUrl = siteInfo?.twitter_url?.trim() || null;
  const instagramUrl = siteInfo?.instagram_url?.trim() || null;
  const youtubeUrl = siteInfo?.youtube_url?.trim() || null;
  const linkedinUrl = siteInfo?.linkedin_url?.trim() || null;
  const hasSocialMedia = Boolean(facebookUrl || twitterUrl || instagramUrl || youtubeUrl || linkedinUrl);

  const whatsappUrl = cleanPhone ? `https://wa.me/${cleanPhone.replace(/^\+/, '')}` : null;

  return (
    <>
      <footer className={`foton-footer ${lang ? 'rtl' : 'ltr'}`}>
        <div className="foton-footer-container">
          {/* Top Row: Official Distributor Tagline + Gulf Great Sands */}
          <div className="foton-footer-top">
            <div className="foton-footer-brand-wrap">
              <span className="foton-footer-tagline">
                {lang ? "الموزع الرسمي لـ فوتون في عمان" : "Official Distributor of Foton in Oman"}
              </span>
              <h2 className="foton-footer-title">
                {lang ? "رمال الخليج الكبرى" : "Gulf Great Sands"}
              </h2>
            </div>
          </div>

          {/* Main Dynamic Columns Grid - Shows ONLY available data */}
          <div className="foton-footer-grid">
            {/* Column 1: QUICKLINKS (only menus where display_in_footer = 1) */}
            {usefulLinks && usefulLinks.length > 0 && (
              <div className="foton-footer-col">
                <h4 className="foton-footer-heading">
                  {lang ? "روابط سريعة" : "QUICKLINKS"}
                </h4>
                <ul className="foton-footer-list">
                  {usefulLinks.map((item) => {
                    const target = item.link ? `/${item.link.replace(/^\/+/, '')}` : (item.slug ? `/${item.slug}` : '#');
                    const label = lang ? (item.menu_name_ar || item.menu_name_en) : item.menu_name_en;
                    return (
                      <li key={item.id || item.slug}>
                        <Link to={target} className="foton-footer-link">
                          {label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Column 2: PRODUCTS (only active products from database) */}
            {productsList && productsList.length > 0 && (
              <div className="foton-footer-col foton-footer-col-products">
                <h4 className="foton-footer-heading">
                  {lang ? "المنتجات" : "PRODUCTS"}
                </h4>
                <ul className="foton-footer-list">
                  {productsList.map((prod) => {
                    const slug = (prod.slug || '').toLowerCase();
                    const categorySlugs = [
                      'mini-truck', 'van', 'mini-bus', 'pickup',
                      'light-duty-trucks', 'medium-duty-truck',
                      'medium-and-heavy-duty-trucks', 'special-vehicles'
                    ];
                    const target = categorySlugs.includes(slug)
                      ? `/vehicles/${slug}`
                      : (prod.link ? `/${prod.link.replace(/^\/+/, '')}` : (prod.slug ? `/${prod.slug}` : '#'));
                    const label = lang ? (prod.menu_name_ar || prod.menu_name_en) : prod.menu_name_en;
                    return (
                      <li key={prod.id || prod.slug}>
                        <Link to={target} className="foton-footer-link">
                          {label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Column 3: CONTACT US (only if contact info or link exists) */}
            {(phoneNumber || emailAddress) && (
              <div className="foton-footer-col">
                <h4 className="foton-footer-heading">
                  {lang ? "اتصل بنا" : "CONTACT US"}
                </h4>
                <ul className="foton-footer-list">
                  <li>
                    <Link to="/contact-us" className="foton-footer-contact-item">
                      <FaUser className="foton-footer-contact-icon" />
                      <span>{lang ? "اتصل بنا" : "Contact us"}</span>
                    </Link>
                  </li>
                  {phoneNumber && (
                    <li>
                      <a href={`tel:${cleanPhone}`} className="foton-footer-contact-item">
                        <FaPhoneAlt className="foton-footer-contact-icon" />
                        <span>{phoneNumber}</span>
                      </a>
                    </li>
                  )}
                  {emailAddress && (
                    <li>
                      <a href={`mailto:${emailAddress}`} className="foton-footer-contact-item">
                        <FaEnvelope className="foton-footer-contact-icon" />
                        <span>{emailAddress}</span>
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Column 4: SOCIAL MEDIA (only shows networks with configured URLs) */}
            {hasSocialMedia && (
              <div className="foton-footer-col">
                <h4 className="foton-footer-heading">
                  {lang ? "وسائل التواصل الاجتماعي" : "SOCIAL MEDIA"}
                </h4>
                <ul className="foton-footer-social-list">
                  {facebookUrl && (
                    <li>
                      <a
                        href={facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="foton-footer-social-link"
                      >
                        <FaFacebookSquare className="foton-footer-social-icon" />
                        <span>Facebook</span>
                      </a>
                    </li>
                  )}
                  {youtubeUrl && (
                    <li>
                      <a
                        href={youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="foton-footer-social-link"
                      >
                        <FaYoutubeSquare className="foton-footer-social-icon" />
                        <span>YouTube</span>
                      </a>
                    </li>
                  )}
                  {instagramUrl && (
                    <li>
                      <a
                        href={instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="foton-footer-social-link"
                      >
                        <FaInstagram className="foton-footer-social-icon" />
                        <span>Instagram</span>
                      </a>
                    </li>
                  )}
                  {twitterUrl && (
                    <li>
                      <a
                        href={twitterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="foton-footer-social-link"
                      >
                        <FaXTwitter className="foton-footer-social-icon" />
                        <span>Twitter / X</span>
                      </a>
                    </li>
                  )}
                  {linkedinUrl && (
                    <li>
                      <a
                        href={linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="foton-footer-social-link"
                      >
                        <FaLinkedin className="foton-footer-social-icon" />
                        <span>LinkedIn</span>
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Bottom Copyright Row */}
          <div className="foton-footer-bottom">
            <p className="foton-footer-copyright-text">
              {lang
                ? `حقوق الطبع والنشر © ${new Date().getFullYear()} ${siteInfo?.site_name_ar || siteInfo?.site_name_en || "فوتون"}. جميع الحقوق محفوظة.`
                : `Copyright © ${new Date().getFullYear()} ${siteInfo?.site_name_en || "Foton"}. All rights reserved.`}
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Side Quick Contact Dock - only shows active contact options */}
      {(facebookUrl || twitterUrl || whatsappUrl || phoneNumber) && (
        <aside className="foton-floating-dock" aria-label="Quick contact links">
          {facebookUrl && (
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="foton-dock-item dock-fb"
              title="Facebook"
              aria-label="Facebook"
            >
              <FaFacebookF />
            </a>
          )}
          {twitterUrl && (
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="foton-dock-item dock-x"
              title="X / Twitter"
              aria-label="X (Twitter)"
            >
              <FaXTwitter />
            </a>
          )}
          {whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="foton-dock-item dock-chat"
              title="WhatsApp / Chat"
              aria-label="WhatsApp Chat"
            >
              <FaWhatsapp />
            </a>
          )}
          {phoneNumber && (
            <a
              href={`tel:${cleanPhone}`}
              className="foton-dock-item dock-phone"
              title="Call Us"
              aria-label="Phone Call"
            >
              <FaPhoneAlt />
            </a>
          )}
        </aside>
      )}
    </>
  );
}

export default Footer;
